import { Link, useParams } from 'react-router-dom';
import { Star } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Thumbnail } from './Thumbnail';
import { SelectionCheckbox } from './SelectionCheckbox';
import { ItemActionMenu } from './ItemActionMenu';
import { useItemListContext } from './ItemListProvider';
import { PreviewToggle } from './PreviewToggle';
import { useItemActions } from '@/hooks/useItemActions';
import type { NormalizedItem } from './types';

interface FileCardProps {
  item: NormalizedItem;
  parentId?: string;
  showPreviewToggle?: boolean;
}

export function FileCard({ item, parentId, showPreviewToggle = false }: FileCardProps) {
  const { isSelected, updateIsQuerying, onRefresh, permissions, sortCriteria, sortAscending } = useItemListContext();
  const { isFavorite } = useItemActions({ updateIsQuerying, onRefresh });
  const params = useParams();
  const selected = isSelected(item.id);
  const favorite = isFavorite(item.id);

  const parentType = parentId ? 'folder' : 'project';
  const pid = parentId || params.id;
  // Use the current route's viewType so prev/next navigation includes all files in the same view
  const viewType = params.viewType || 'allfiles';
  const sortQuery = `?sortBy=${sortCriteria}&ascending=${sortAscending}`;
  const linkTo = pid
    ? `/in/${parentType}/${pid}/resource/${item.id}/${viewType}${sortQuery}`
    : undefined;

  const canTogglePreview = showPreviewToggle && permissions.canEdit && item.isRendable;

  const card = (
    <div
      className={cn(
        'group relative rounded-xl border transition-all',
        selected
          ? 'border-primary bg-primary-muted shadow-md'
          : 'border-border/60 bg-card shadow-sm hover:shadow-md hover:border-border',
      )}
    >
      <Thumbnail item={item} size="lg" className="aspect-video w-full overflow-hidden rounded-t-[11px]" />
      <div className="flex items-center gap-2 px-3 py-2">
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          {item.title || item.name}
        </p>
        {favorite && <Star className="h-3.5 w-3.5 shrink-0 fill-yellow-400 text-yellow-400" />}
        {canTogglePreview && <PreviewToggle item={item} />}
        <ItemActionMenu item={item} />
      </div>
      <div className="absolute left-2 top-2">
        <SelectionCheckbox item={item} />
      </div>
    </div>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="block no-underline">
        {card}
      </Link>
    );
  }

  return card;
}
